/**
 * Utility functions for controller_dropdown variant config resolution.
 *
 * A controller_dropdown field acts as a master control — its selected value
 * determines the config (min/max, options) and visibility of other fields
 * in the same widget via per-value variant overrides.
 */

interface FieldLike {
  field_type: string;
  config_data: Record<string, any>;
}

interface ResolvedConfig {
  config: Record<string, any>;
  hidden: boolean;
}

/**
 * Finds the single controller_dropdown field from a fields array.
 * Returns undefined if none exists.
 */
export function getControllerField<T extends FieldLike>(fields: T[]): T | undefined {
  return fields.find(f => f.field_type === 'controller_dropdown');
}

/**
 * Merges base config with a variant override for a given controller value.
 * Returns the resolved config and whether the field should be hidden.
 *
 * - If controllerValue is null/undefined or no matching variant exists, returns base config as-is.
 * - Variant overrides are shallow-merged on top of base config.
 * - The `hidden` flag from the variant controls field visibility (not merged into config).
 */
export function resolveFieldConfig(
  baseConfig: Record<string, any>,
  variants: Record<string, any> | undefined | null,
  controllerValue: string | undefined | null
): ResolvedConfig {
  if (!controllerValue || !variants || !variants[controllerValue]) {
    return { config: baseConfig, hidden: false };
  }
  const variant = variants[controllerValue];
  const { hidden, ...overrides } = variant;
  return {
    config: { ...baseConfig, ...overrides },
    hidden: !!hidden,
  };
}

/**
 * Convenience wrapper: extracts variants from field.config_data and calls resolveFieldConfig.
 */
export function getEffectiveConfig(
  field: FieldLike,
  controllerValue: string | undefined | null
): ResolvedConfig {
  const { variants, ...baseConfig } = field.config_data;
  return resolveFieldConfig(baseConfig, variants, controllerValue);
}
